import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from '@/features/auth/protected-route';
import { TenantShell } from './layout/tenant-shell';
import { PlatformShell } from './layout/platform-shell';
import { WorkspacePickerRoute } from '@/routes/workspace';
import { NoAccessRoute } from '@/routes/no-access';
import { PlatformHomeRoute } from '@/routes/platform';
import { LoginRoute } from '@/routes/auth/login';
import { LogoutRoute } from '@/routes/auth/logout';
import { DashboardFinancieroRoute } from '@/routes/dashboard/financiero';
import { NuevoCondominioRoute } from '@/routes/condominios/new';
import { CondominioSettingsRoute } from '@/routes/condominios/settings';
import { AdministradoresRoute } from '@/routes/condominios/administradores';
import { UnidadesRoute } from '@/routes/unidades';
import { NuevaUnidadRoute } from '@/routes/unidades/nueva';
import { UnidadDetailRoute } from '@/routes/unidades/detail';
import { ResidentesRoute } from '@/routes/residentes';
import { ResidenteDetailRoute } from '@/routes/residentes/detail';
import { GastosComunesListRoute } from '@/routes/gastos-comunes';
import { NuevoGastoComunRoute } from '@/routes/gastos-comunes/nuevo';
import { GastoComunDetailRoute } from '@/routes/gastos-comunes/detail';
import { EditarBorradorRoute } from '@/routes/gastos-comunes/editar';
import { MisCargosRoute } from '@/routes/mis-cargos';
import { IniciarPagoRoute } from '@/routes/pagos/iniciar';
import { MockCheckoutRoute } from '@/routes/pagos/mock-checkout';
import { AsambleasListRoute } from '@/routes/asambleas';
import { NuevaAsambleaRoute } from '@/routes/asambleas/nueva';
import { AsambleaDetailRoute } from '@/routes/asambleas/detail';
import { ActaRoute } from '@/routes/asambleas/acta';
import { AsambleaResultadosRoute } from '@/routes/asambleas/resultados';
import { AuditLogRoute } from '@/routes/audit-log';
import { MercadoFeedRoute } from '@/routes/mercado';
import { PublicarRoute } from '@/routes/mercado/publicar';
import { MercadoDetailRoute } from '@/routes/mercado/detail';
import { ChatsListRoute } from '@/routes/mercado/chats';
import { MisPublicacionesRoute } from '@/routes/mercado/mis-publicaciones';
import { VendedorRoute } from '@/routes/mercado/vendedor';
import { VisitasRoute } from '@/routes/visitas';
import { CrearVisitaRoute } from '@/routes/visitas/crear';
import { PaquetesRoute } from '@/routes/paquetes/index';
import { NuevoPaqueteRoute } from '@/routes/paquetes/nuevo';
import { PaqueteDetailRoute } from '@/routes/paquetes/$id';
import { UnidadPaquetesRoute } from '@/routes/unidades/$unidadId.paquetes';
import { VerificarRoute } from '@/routes/verificar';
import { FondoReservaRoute } from '@/routes/fondo-reserva';
import { MovimientosFondoRoute } from '@/routes/fondo-reserva/movimientos';
import { VerificarIntegridadRoute } from '@/routes/fondo-reserva/verificar';
import { RootIndex } from '@/routes/_index';
import { NotFoundRoute } from '@/routes/not-found';

// React Router v7 data mode: createBrowserRouter with nested routes.
// Loaders are thin — feature hooks use TanStack Query.
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootIndex />,
  },
  {
    path: '/login',
    element: <LoginRoute />,
  },
  {
    path: '/logout',
    element: <LogoutRoute />,
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <Outlet />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="workspace" replace /> },

      // Workspace picker: auto-skips for super_admin (→ platform) or single tenant.
      { path: 'workspace', element: <WorkspacePickerRoute /> },

      // No-access empty state: no tenants assigned.
      { path: 'no-access', element: <NoAccessRoute /> },

      // Platform subtree: super_admin only, managed by PlatformShell.
      {
        path: 'platform',
        element: <PlatformShell />,
        children: [
          { index: true, element: <PlatformHomeRoute /> },
          { path: 'condominios/nuevo', element: <NuevoCondominioRoute /> },
        ],
      },

      // Tenant subtree: all authenticated tenant-scoped views.
      // TenantShell reads :condoId, calls setActiveTenant, and checks membership.
      {
        path: 'condominios/:condoId',
        element: <TenantShell />,
        children: [
          // Index → dashboard financiero
          { index: true, element: <DashboardFinancieroRoute /> },

          { path: 'settings', element: <CondominioSettingsRoute /> },

          // Administradores (super_admin gated — stub for Task 9.2)
          { path: 'administradores', element: <AdministradoresRoute /> },

          // Directorio
          { path: 'unidades', element: <UnidadesRoute /> },
          { path: 'unidades/nueva', element: <NuevaUnidadRoute /> },
          { path: 'unidades/:unidadId', element: <UnidadDetailRoute /> },
          { path: 'residentes', element: <ResidentesRoute /> },
          { path: 'residentes/:residenteId', element: <ResidenteDetailRoute /> },

          // Finanzas
          { path: 'gastos-comunes', element: <GastosComunesListRoute /> },
          { path: 'gastos-comunes/nuevo', element: <NuevoGastoComunRoute /> },
          { path: 'gastos-comunes/:gastoId', element: <GastoComunDetailRoute /> },
          { path: 'gastos-comunes/:gastoId/editar', element: <EditarBorradorRoute /> },

          { path: 'fondo-reserva', element: <FondoReservaRoute /> },
          { path: 'fondo-reserva/movimientos', element: <MovimientosFondoRoute /> },
          { path: 'fondo-reserva/verificar', element: <VerificarIntegridadRoute /> },

          { path: 'mis-cargos', element: <MisCargosRoute /> },

          { path: 'pagos/iniciar/:cargoId', element: <IniciarPagoRoute /> },
          { path: 'pagos/mock-checkout/:pagoId', element: <MockCheckoutRoute /> },

          // Gobernanza
          { path: 'asambleas', element: <AsambleasListRoute /> },
          { path: 'asambleas/nueva', element: <NuevaAsambleaRoute /> },
          { path: 'asambleas/:asambleaId', element: <AsambleaDetailRoute /> },
          { path: 'asambleas/:asambleaId/acta', element: <ActaRoute /> },
          { path: 'asambleas/:asambleaId/resultados', element: <AsambleaResultadosRoute /> },

          { path: 'audit-log', element: <AuditLogRoute /> },

          // Visitas (pases de visitante)
          { path: 'visitas', element: <VisitasRoute /> },
          { path: 'visitas/crear', element: <CrearVisitaRoute /> },

          // Paquetes y Encomiendas
          { path: 'paquetes', element: <PaquetesRoute />, handle: { breadcrumb: 'Paquetes', pageTitle: 'Paquetes' } },
          { path: 'paquetes/nuevo', element: <NuevoPaqueteRoute />, handle: { breadcrumb: 'Nuevo paquete', pageTitle: 'Nuevo paquete' } },
          { path: 'paquetes/:id', element: <PaqueteDetailRoute />, handle: { breadcrumb: 'Detalle', pageTitle: 'Paquete' } },
          { path: 'unidades/:unidadId/paquetes', element: <UnidadPaquetesRoute />, handle: { breadcrumb: 'Paquetes de unidad' } },

          // Mercado Vecinal
          { path: 'mercado', element: <MercadoFeedRoute /> },
          { path: 'mercado/publicar', element: <PublicarRoute /> },
          { path: 'mercado/mis-publicaciones', element: <MisPublicacionesRoute /> },
          { path: 'mercado/chats', element: <ChatsListRoute /> },
          { path: 'mercado/chats/:threadId', element: <ChatsListRoute /> },
          { path: 'mercado/vendedor/:sellerId', element: <VendedorRoute /> },
          { path: 'mercado/:listingId', element: <MercadoDetailRoute /> },
        ],
      },
    ],
  },
  {
    path: '/verificar/:token',
    element: <VerificarRoute />,
  },
  { path: '*', element: <NotFoundRoute /> },
]);
